import React from 'react';
import { Navigate } from 'react-router-dom';
import { isSuperAdmin } from "../../utils/permissions";
import { useUser } from '../../contexts/UserContext';

/**
 * FirstTimeUserGuard - Redirects new users to onboarding
 * 
 * Checks if user has completed onboarding or has existing data
 */
export function FirstTimeUserGuard({ children }) {
	const { user, isLoading: userLoading } = useUser();

	// Only gate on auth resolution. We must NOT block on the DataContext
	// clients/accounts fetch - the decision is purely user.onboardingCompleted,
	// and gating on `loading` was serializing a ~1.5s fetch in front of every
	// dashboard paint (the "Loading…" first impression). The page fetches its
	// own data with its own loading state.
	if (userLoading) {
		return (
			<div className="min-h-screen bg-muted flex items-center justify-center">
				<div className="text-center">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500 mx-auto mb-4"></div>
					<p className="text-muted-foreground">Loading...</p>
				</div>
			</div>
		);
	}

	// If user has explicitly completed onboarding, don't redirect
	if (user?.onboardingCompleted) {
		return children;
	}

	// Don't skip onboarding based on existing clients - require explicit completion

	// Redirect to onboarding if user hasn't completed it
	return <Navigate to="/onboarding" replace />;
}

/**
 * DashboardGuard - Redirects new users away from dashboard to onboarding
 * 
 * Used on dashboard to ensure new users complete onboarding first
 */
export function DashboardGuard({ children }) {
	const { user, isLoading: userLoading } = useUser();

	// Only gate on auth resolution. We must NOT block on the DataContext
	// clients/accounts fetch - the decision is purely user.onboardingCompleted,
	// and gating on `loading` was serializing a ~1.5s fetch in front of every
	// dashboard paint (the "Loading…" first impression). The page fetches its
	// own data with its own loading state.
	if (userLoading) {
		return (
			<div className="min-h-screen bg-muted flex items-center justify-center">
				<div className="text-center">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500 mx-auto mb-4"></div>
					<p className="text-muted-foreground">Loading...</p>
				</div>
			</div>
		);
	}

	// If user has explicitly completed onboarding, allow access
	if (user?.onboardingCompleted) {
		return children;
	}

	// Don't allow access based on existing clients - require explicit onboarding completion

	// Redirect to onboarding if user hasn't completed it
	return <Navigate to="/onboarding" replace />;
}

/**
 * GlobalAdminGuard - Prevents non-global-admin users from accessing admin pages
 *
 * Checks if user has 'global-admin' in their canAccess array
 */
export function GlobalAdminGuard({ children }) {
  const { user, isLoading, isAuthenticated } = useUser();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated → redirect to login
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  // Authenticated but not RepCheck staff → redirect to dashboard
  if (!isSuperAdmin(user)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

/**
 * OnboardingGuard - Prevents completed users from accessing onboarding
 * 
 * Redirects users who have already completed onboarding
 */
export function OnboardingGuard({ children }) {
	const { user, isLoading: userLoading } = useUser();

	// Only gate on auth resolution. We must NOT block on the DataContext
	// clients/accounts fetch - the decision is purely user.onboardingCompleted,
	// and gating on `loading` was serializing a ~1.5s fetch in front of every
	// dashboard paint (the "Loading…" first impression). The page fetches its
	// own data with its own loading state.
	if (userLoading) {
		return (
			<div className="min-h-screen bg-muted flex items-center justify-center">
				<div className="text-center">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500 mx-auto mb-4"></div>
					<p className="text-muted-foreground">Loading...</p>
				</div>
			</div>
		);
	}

	// If user has explicitly completed onboarding, redirect to dashboard
	if (user?.onboardingCompleted) {
		return <Navigate to="/dashboard" replace />;
	}

	// Don't redirect based on existing clients - let new users go through onboarding
	// even if there's existing data in the system

	// Allow access to onboarding if user hasn't completed it
	return children;
} 